import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { todasLasLocaciones, type Location } from '@/engine/map';
import { useWorldStore } from '@/engine/state';
import { FullscreenPage } from '@/components/ui';
import { cn } from '@/utils/cn';

export function WorldMapView() {
  const locacionActual = useWorldStore((s) => s.locacionActual);
  const desbloqueadas = useWorldStore((s) => s.locacionesDesbloqueadas);
  const irALocacion = useWorldStore((s) => s.irALocacion);

  const [seleccionada, setSeleccionada] = useState<Location | null>(null);

  const visibles = useMemo(
    () => todasLasLocaciones().filter((l) => !l.oculta || desbloqueadas.includes(l.id)),
    [desbloqueadas],
  );

  const conexiones = useMemo(() => {
    const pares: Array<[Location, Location]> = [];
    const set = new Set(visibles.map((l) => l.id));
    for (const a of visibles) {
      for (const id of a.conexiones) {
        if (!set.has(id)) continue;
        if (a.id < id) {
          const b = visibles.find((x) => x.id === id);
          if (b) pares.push([a, b]);
        }
      }
    }
    return pares;
  }, [visibles]);

  const viajar = (loc: Location) => {
    if (!desbloqueadas.includes(loc.id)) return;
    irALocacion(loc.id);
    setSeleccionada(loc);
  };

  return (
    <FullscreenPage
      titulo="Mapa del mundo"
      subtitulo={`${desbloqueadas.length} de ${todasLasLocaciones().length} locaciones descubiertas`}
      bodyScroll={false}
    >
      <div className="h-full w-full grid grid-rows-[1fr_auto] md:grid-rows-1 md:grid-cols-[minmax(0,1fr)_minmax(14rem,18rem)] gap-3 min-h-0 min-w-0">
        {/* Mapa */}
        <div className="relative rounded-lg border border-ash-800 bg-gradient-to-br from-ash-900 via-ash-950 to-mystic-950 overflow-hidden min-h-0 min-w-0">
          <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden>
            {conexiones.map(([a, b]) => (
              <line
                key={`${a.id}-${b.id}`}
                x1={`${a.posicion.x}%`}
                y1={`${a.posicion.y}%`}
                x2={`${b.posicion.x}%`}
                y2={`${b.posicion.y}%`}
                stroke="rgba(183,180,170,0.2)"
                strokeWidth={1}
                strokeDasharray="3 4"
              />
            ))}
          </svg>

          {visibles.map((loc) => {
            const esActual = loc.id === locacionActual;
            const desbloqueada = desbloqueadas.includes(loc.id);
            return (
              <button
                key={loc.id}
                type="button"
                onClick={() => viajar(loc)}
                onMouseEnter={() => setSeleccionada(loc)}
                onFocus={() => setSeleccionada(loc)}
                className="absolute group outline-none"
                style={{
                  left: `${loc.posicion.x}%`,
                  top: `${loc.posicion.y}%`,
                  transform: 'translate(-50%, -100%)',
                }}
                aria-label={loc.nombre}
              >
                {/* Contenedor del pin: altura fija, scale solo en el ícono */}
                <div className="relative flex flex-col items-center">
                  <motion.span
                    className="flex"
                    whileHover={{ scale: 1.2 }}
                    whileFocus={{ scale: 1.2 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    <MapPin
                      className={cn(
                        'w-5 h-5 md:w-6 md:h-6 drop-shadow-lg',
                        esActual
                          ? 'text-ember-300 animate-glow-pulse'
                          : desbloqueada
                            ? 'text-moss-400 group-hover:text-moss-300'
                            : 'text-ash-600',
                      )}
                    />
                  </motion.span>
                  <span
                    className={cn(
                      'absolute top-full mt-0.5 whitespace-nowrap fluid-xs uppercase tracking-widest font-ui pointer-events-none',
                      esActual ? 'text-ember-300' : 'text-ash-400',
                    )}
                  >
                    {loc.nombre}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <aside className="rounded-lg border border-ash-800 bg-ash-950/60 p-4 overflow-y-auto scroll-y min-h-0">
          {seleccionada ? (
            <motion.div
              key={seleccionada.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <p className="fluid-xs uppercase tracking-widest text-ash-500 font-ui">
                {desbloqueadas.includes(seleccionada.id) ? 'Desbloqueada' : 'Por descubrir'}
              </p>
              <h2 className="text-display fluid-lg text-ash-100 mt-1">
                {seleccionada.nombre}
              </h2>
              <p className="fluid-sm text-ash-300 mt-2 text-narrative">
                {seleccionada.descripcion}
              </p>
              {seleccionada.id === locacionActual ? (
                <p className="mt-3 fluid-xs uppercase tracking-widest text-ember-400 font-ui">
                  Estás acá ahora.
                </p>
              ) : desbloqueadas.includes(seleccionada.id) ? (
                <button
                  type="button"
                  onClick={() => viajar(seleccionada)}
                  className="mt-3 px-4 py-1.5 rounded bg-moss-700 hover:bg-moss-600 text-ash-50 font-ui fluid-xs uppercase tracking-wide"
                >
                  Viajar acá
                </button>
              ) : null}
            </motion.div>
          ) : (
            <p className="fluid-sm italic text-ash-500 font-ui">
              Tocá un pin para ver detalles.
            </p>
          )}
        </aside>
      </div>
    </FullscreenPage>
  );
}
